import React from 'react';
import { useLocation } from 'react-router-dom';

import { PageShell } from '../../../../components/PageShell';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import { SlideSwap, type NavDir } from '../../../../transitions';
import { HeaderActionProvider, useHeaderActionHost } from '../../header-action';
import { WEBSITE_HUB_FLAG, WEBSITE_TABS, websiteTabIndex } from '../../tabs';
import { WebsiteAreaHeader } from '../WebsiteAreaHeader';
import { WebsiteTabs } from '../WebsiteTabs';
import { Column, Content, HeaderActionSlot } from './WebsiteShell.styles';

/** True inside the Website section's shared frame: an area's page then renders only its content. */
const InWebsiteShell = React.createContext(false);

/**
 * The Website section's frame, shared by its area routes (router.tsx wraps each in it, and PageTransition does not
 * slide between them): the page shell and the section's header render once and stay put, while the open area's
 * content slides beneath them, in from the right when the new area sits further right in the areas' order and from
 * the left when it sits further left. With the `website-hub` flag on, the header is the area header (the back button to
 * the hub and the area's name) and an area is reached from the hub or an in-page link (e.g. Art Style's logo card
 * to Logos); off, it is the tab row, as before the hub. A new area opens at the top of the page, as its own route did.
 */
export const WebsiteShell = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const { pathname } = useLocation();
  const { isEnabled } = useFeatureGate();
  const hub = isEnabled(WEBSITE_HUB_FLAG);
  const index = websiteTabIndex(pathname);
  /** The tab on screen before this render: the slide's direction is read against it. */
  const shownRef = React.useRef(index);
  const shown = shownRef.current;
  // Leaving the section, the outgoing frame re-renders under the next route (the transitions do not freeze it): it
  // stays on its last tab, so it neither swaps in a fresh copy of that page nor resets the scroll.
  const current = index === -1 ? shown : index;
  const token = current === -1 ? pathname : WEBSITE_TABS[current].id;
  const direction: NavDir =
    current === -1 || shown === -1 || current === shown ? 'none' : current > shown ? 'forward' : 'back';
  // With the hub, a flagged-off area's route answers with the not-found page, which gets no area header (the hub hides
  // its card under the same flag). The tab row, without the hub, shows over it as it always did.
  const headerTab = current !== -1 && isEnabled(WEBSITE_TABS[current].flag) ? WEBSITE_TABS[current] : null;
  const contentRef = React.useRef<HTMLDivElement>(null);
  // The area header's action slot, drawn while the open area's page asks for it (see `useWebsiteHeaderAction`).
  const headerAction = useHeaderActionHost(hub && headerTab !== null);

  // The pinned area header's whole box is published to the area's content (see Content), measured: on a phone its action
  // sits on a row of its own under the title, so the header is far taller than its title's line.
  const backHeader = hub && headerTab !== null;
  React.useLayoutEffect(() => {
    const content = contentRef.current;
    const header = backHeader ? content?.previousElementSibling : null;
    if (!content || !(header instanceof HTMLElement)) return undefined;
    const measure = (): void => content.style.setProperty('--ows-back-header-offset', `${header.offsetHeight}px`);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(header);
    return () => {
      observer?.disconnect();
      content.style.removeProperty('--ows-back-header-offset');
    };
  }, [backHeader]);

  React.useLayoutEffect(() => {
    if (index === -1 || shownRef.current === index) return;
    shownRef.current = index;
    // The studio's scroller on desktop, the document on a phone.
    const scroller = contentRef.current?.closest<HTMLElement>('.ai-bb-page-root');
    if (scroller) scroller.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [index]);

  return (
    <InWebsiteShell.Provider value>
      <HeaderActionProvider value={headerAction.value}>
        <PageShell>
          {!hub ? (
            <WebsiteTabs />
          ) : headerTab ? (
            <WebsiteAreaHeader
              tab={headerTab}
              action={headerAction.wanted ? <HeaderActionSlot ref={headerAction.setSlot} /> : undefined}
              override={headerAction.override}
            />
          ) : null}
          <Content ref={contentRef} $backHeader={backHeader}>
            <SlideSwap token={token} direction={direction}>
              <Column>{children}</Column>
            </SlideSwap>
          </Content>
        </PageShell>
      </HeaderActionProvider>
    </InWebsiteShell.Provider>
  );
};

export type WebsitePageProps = {
  styleText?: string;
  className?: string;
  /** Shows the Website section's header, the area header with the hub and the tab row without it (false where the page
   *  serves another section, e.g. Marketing Assets). */
  header?: boolean;
  children: React.ReactNode;
};

/**
 * A Website area page's frame. Inside {@link WebsiteShell} the page renders only its content (the shell owns the page
 * shell and the section's header); anywhere else it renders its own page shell, headed, unless `header` is false, by
 * the area header of the area whose route is open (none when no area's route is) with the `website-hub` flag on, and
 * by the tab row with it off.
 */
export const WebsitePage = ({
  styleText = '',
  className,
  header = true,
  children,
}: WebsitePageProps): React.ReactElement => {
  const inShell = React.useContext(InWebsiteShell);
  const { pathname } = useLocation();
  const { isEnabled } = useFeatureGate();
  const hub = isEnabled(WEBSITE_HUB_FLAG);
  const index = header && hub ? websiteTabIndex(pathname) : -1;
  const headerAction = useHeaderActionHost(!inShell && index !== -1);
  if (inShell) return <React.Fragment>{children}</React.Fragment>;
  return (
    <HeaderActionProvider value={headerAction.value}>
      <PageShell styleText={styleText} className={className}>
        {header && !hub ? <WebsiteTabs /> : null}
        {index !== -1 ? (
          <WebsiteAreaHeader
            tab={WEBSITE_TABS[index]}
            action={headerAction.wanted ? <HeaderActionSlot ref={headerAction.setSlot} /> : undefined}
            override={headerAction.override}
          />
        ) : null}
        {children}
      </PageShell>
    </HeaderActionProvider>
  );
};

export default WebsiteShell;
